import React, { createContext, useState, useContext, useEffect } from 'react';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth Provider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in when app starts
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Mock function to check auth status
  const checkAuthStatus = () => {
    try {
      const savedUser = localStorage.getItem('vat_demo_user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get permissions based on role
  const getPermissionsByRole = (role) => {
    const permissions = {
      admin: {
        transactions: ['create', 'read', 'update', 'delete', 'import', 'export'],
        returns: ['create', 'read', 'update', 'delete', 'file'],
        reports: ['create', 'read', 'export', 'schedule'],
        users: ['create', 'read', 'update', 'delete'],
        settings: ['read', 'update']
      },
      manager: {
        transactions: ['create', 'read', 'update', 'import', 'export'],
        returns: ['create', 'read', 'update', 'file'],
        reports: ['create', 'read', 'export'],
        users: ['read'],
        settings: ['read']
      },
      viewer: {
        transactions: ['read', 'export'],
        returns: ['read'],
        reports: ['read', 'export'],
        users: [],
        settings: ['read']
      }
    };
    
    return permissions[role] || permissions.viewer;
  };

// Mock login function
const login = (email, role = 'viewer') => {
  console.log('Login function called with:', email, role); // Debug log
  
  const userData = {
    email: email,
    role: role,
    permissions: getPermissionsByRole(role),
    name: email.split('@')[0]
  };
  
  console.log('Setting user data:', userData); // Debug log
  setUser(userData);
  localStorage.setItem('vat_demo_user', JSON.stringify(userData));
  
  console.log('User set in state and localStorage'); // Debug log
  return userData;
};

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('vat_demo_user');
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}